import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * A multi-line field. `Input`'s note explains the fill-at-rest, ring-on-focus treatment and why
 * it replaced the underline; this is the same field with room for more than one line.
 *
 * `rounded-2xl` rather than `Input`'s pill, and the same radius as `ItemCard`: a pill on a
 * multi-line box puts the first and last lines inside curves while the middle ones run to the
 * full width, so the text no longer sits on a straight left edge.
 */
function Textarea({ className, ...props }: React.ComponentProps<"textarea">) {
  return (
    <div
      className={cn(
        // A visible edge at rest, not a transparent one.
        //
        // The fill alone does not define the field everywhere it is used: `--card` and
        // `--secondary` are the SAME value in the dark palette, so a secondary-filled field on a
        // card — the sign-in, unlock and account panels, all of them — was a field you could not
        // see. The old underline hid this, because a rule is an edge even when the fill matches
        // its ground. `--border` is distinct from both in both themes, so the field defines
        // itself wherever it is put, and focus then only has to change the edge's colour.
        "group flex w-full flex-col rounded-2xl border border-border bg-secondary p-4",
        "transition-[background-color,border-color,box-shadow] duration-200",
        "[&:hover:not(:focus-within)]:bg-accent",
        "focus-within:border-foreground focus-within:bg-background focus-within:ring-1 focus-within:ring-foreground",
        "has-[[aria-invalid=true]]:border-destructive has-[[aria-invalid=true]]:ring-1 has-[[aria-invalid=true]]:ring-destructive",
        "has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50",
        className,
      )}
    >
      <textarea
        data-slot="textarea"
        className={cn(
          "w-full flex-1 bg-transparent text-base leading-6 text-foreground",
          "placeholder:text-muted-foreground/50 outline-none resize-none",
          "disabled:cursor-not-allowed",
          "aria-invalid:text-destructive no-scrollbar",
        )}
        {...props}
      />
    </div>
  )
}

export { Textarea }
